import { MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';

class CrudAPIClient {
  protected baseURL: string | undefined;

  constructor(apiUrl: string | undefined) {
    this.baseURL = apiUrl;
  }

  protected transformList<T = any>(nodes: Array<T>): Array<T> {
    return nodes.map(node => this.transformNode(node));
  }

  protected transformNode<T = any>(node: T): T {
    return node;
  }

  protected resolveParameterizedUrlVariables(meta: MetaQuery | undefined): string | undefined {
    return this.baseURL;
  }

  protected getRequestConfig(meta: MetaQuery | undefined) {
    const headers = (meta as { headers?: Record<string, string> } | undefined)?.headers;
    if (!headers) {
      return undefined;
    }
    return { headers };
  }

  async findById<T = any>(id: string | number, meta: MetaQuery | undefined): Promise<T> {
    try {
      const url = this.resolveParameterizedUrlVariables(meta);
      const response = await axiosInstance.get(`${url}/${id}`, this.getRequestConfig(meta));
      return this.transformNode<T>(response.data.data);
    } catch (error) {
      console.error(`Error fetching entity with ID ${id}:`, error);
      throw error;
    }
  }

  async create<T = any>(payload: Partial<T>, meta: MetaQuery | undefined): Promise<T> {
    try {
      const url = this.resolveParameterizedUrlVariables(meta);
      const response = await axiosInstance.post(
        `${url}/`,
        payload,
        this.getRequestConfig(meta),
      );
      return this.transformNode<T>(response.data.data);
    } catch (error) {
      console.error(`Error creating entity:`, error);
      throw error;
    }
  }

  async upsert<T = any>(payload: Partial<T>, meta: MetaQuery | undefined): Promise<T> {
    try {
      const url = this.resolveParameterizedUrlVariables(meta);
      const response = await axiosInstance.post(
        `${url}/upsert`,
        payload,
        this.getRequestConfig(meta),
      );
      return this.transformNode<T>(response.data.data);
    } catch (error) {
      console.error(`Error creating entity:`, error);
      throw error;
    }
  }

  async updateById<T = any>(
    id: string | number,
    data: Partial<T>,
    meta: MetaQuery | undefined,
  ): Promise<T> {
    try {
      const url = this.resolveParameterizedUrlVariables(meta);
      const response = await axiosInstance.put(
        `${url}/${id}`,
        data,
        this.getRequestConfig(meta),
      );
      return this.transformNode<T>(response.data.data);
    } catch (error) {
      console.error(`Error updating entity with ID ${id}:`, error);
      throw error;
    }
  }

  async deleteById<T = any>(id: string | number, meta: MetaQuery | undefined): Promise<T> {
    try {
      const url = this.resolveParameterizedUrlVariables(meta);
      const response = await axiosInstance.delete(`${url}/${id}`, this.getRequestConfig(meta));
      return this.transformNode<T>(response.data.data);
    } catch (error) {
      console.error(`Error deleting entity with ID ${id}:`, error);
      throw error;
    }
  }

  async search<T = any, Q = any>(
    query: Q,
    page: number,
    size: number,
    meta: MetaQuery | undefined,
  ): Promise<Array<T>> {
    try {
      const url = this.resolveParameterizedUrlVariables(meta);
      const response = await axiosInstance.post(
        `${url}/search?page=${page}&size=${size}`,
        query,
        this.getRequestConfig(meta),
      );
      return this.transformList<T>(response.data.data);
    } catch (error) {
      console.error(`Error searching entities with Query:`, query, error);
      throw error;
    }
  }

  async findBulk<T = any>(
    ids: Array<string | number>,
    meta: MetaQuery | undefined,
  ): Promise<Array<T>> {
    try {
      const url = this.resolveParameterizedUrlVariables(meta);
      const response = await axiosInstance.post(
        `${url}/findBulk`,
        ids,
        this.getRequestConfig(meta),
      );
      return this.transformList<T>(response.data.data);
    } catch (error) {
      console.error(`Error searching entities with ids:{}`, ids, error);
      throw error;
    }
  }

  async createMany<T = any>(
    payload: Array<Partial<T>>,
    meta: MetaQuery | undefined,
  ): Promise<Array<T>> {
    try {
      const url = this.resolveParameterizedUrlVariables(meta);
      const response = await axiosInstance.post(
        `${url}/createBulk`,
        payload,
        this.getRequestConfig(meta),
      );
      return this.transformList<T>(response.data.data);
    } catch (error) {
      console.error(`Error creating entities in bulk:`, error);
      throw error;
    }
  }
}

export default CrudAPIClient;
